import Vue from 'vue'

class OrganizationDashboardRepository {

  getDashboard(orgId, { period = '30d', scope = 'self', filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getTopDrivers(orgId, { period = '30d', scope = 'self', sortBy = 'energy', sortDir = 'desc', page, limit, filterMode, segment } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/users?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&sortBy=${encodeURIComponent(sortBy)}`
      if (sortDir) url += `&sortDir=${encodeURIComponent(sortDir)}`
      if (page) url += `&page=${page}`
      if (limit) url += `&limit=${limit}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      if (segment && segment !== 'all') url += `&segment=${encodeURIComponent(segment)}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getDashboardDetail(orgId, section, { period = '30d', scope = 'self', page = 1, limit = 10, sortBy = null, sortDir = null, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/${section}?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&page=${page}&limit=${limit}`
      if (sortBy) {
        url += `&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir || 'desc')}`
      }
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getPeakLoad(orgId, { period = '30d', scope = 'self', metric = 'sessions', slotSize = 2, limit = 6, errorType, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/peak-load?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&metric=${encodeURIComponent(metric)}&slotSize=${slotSize}&limit=${limit}`
      if (metric === 'errors' && errorType) {
        url += `&errorType=${encodeURIComponent(errorType)}`
      }
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getFleetDetail(orgId, { period = '30d', scope = 'self', status = 'all', page = 1, limit = 10, sortBy = null, sortDir = null, search = null, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/fleet?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`
      if (sortBy) {
        url += `&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir || 'desc')}`
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getEcoImpact(orgId, { period = '30d', scope = 'self' } = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/organization/${orgId}/dashboard/eco?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getMemberDetail(orgId, memberId, { period = '30d', scope = 'self' } = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/organization/${orgId}/dashboard/member/${encodeURIComponent(memberId)}?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }

  getDistribution(orgId, { period = '30d', scope = 'self', metric = 'energy', bins = 10, segment } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/distribution?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&metric=${encodeURIComponent(metric)}&bins=${bins}`
      if (segment && segment !== 'all') url += `&segment=${encodeURIComponent(segment)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getDistributionBin(orgId, { period = '30d', scope = 'self', metric = 'energy', rangeStart, rangeEnd, page = 1, limit = 20, sortBy = 'energy', sortDir = 'desc', segment } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/distribution/bin?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&metric=${encodeURIComponent(metric)}&rangeStart=${rangeStart}&rangeEnd=${rangeEnd}&page=${page}&limit=${limit}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`
      if (segment && segment !== 'all') url += `&segment=${encodeURIComponent(segment)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getHealthDrilldown(orgId, { category = 'incident', scope = 'self', page = 1, limit = 20, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/health/drilldown?category=${encodeURIComponent(category)}&scope=${encodeURIComponent(scope)}&page=${page}&limit=${limit}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getDataQualityDrilldown(orgId, { period = '30d', scope = 'self', page = 1, limit = 20, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/data-quality/drilldown?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&page=${page}&limit=${limit}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getEfficiencyDrilldown(orgId, { period = '30d', scope = 'self', tier = 'low', page = 1, limit = 20, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/efficiency/drilldown?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&tier=${encodeURIComponent(tier)}&page=${page}&limit=${limit}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({
        url,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getHealthTrend(orgId, { period = '30d', scope = 'self', filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/health/trend?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getIncidents(orgId, { period = '30d', scope = 'self', filterMode, severity, page = 1, limit = 10 } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/health/incidents?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}&page=${page}&limit=${limit}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      if (severity) url += `&severity=${encodeURIComponent(severity)}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getReliability(orgId, { period = '30d', scope = 'self', filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/reliability?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getFleetInventory(orgId, { scope = 'self', filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/fleet/inventory?scope=${encodeURIComponent(scope)}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getSimUsage(orgId, { period = '30d', scope = 'self' } = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/organization/${orgId}/dashboard/sim/usage?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getFirmwareDrilldown(orgId, { scope = 'self', filterMode, vendor, model, version, status = 'all', page = 1, limit = 20, sortBy, sortDir } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/fleet/inventory/drilldown?scope=${encodeURIComponent(scope)}&status=${encodeURIComponent(status)}&page=${page}&limit=${limit}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      if (vendor) url += `&vendor=${encodeURIComponent(vendor)}`
      if (model) url += `&model=${encodeURIComponent(model)}`
      if (version) url += `&version=${encodeURIComponent(version)}`
      if (sortBy) url += `&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir || 'asc')}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getSimDrilldown(orgId, { scope = 'self', period = '30d', type, bucket, page = 1, limit = 20, sortBy, sortDir, filterMode } = {}) {
    return new Promise((resolve, reject) => {
      let url = `/api/organization/${orgId}/dashboard/sim/drilldown?scope=${encodeURIComponent(scope)}&period=${encodeURIComponent(period)}&type=${encodeURIComponent(type)}&page=${page}&limit=${limit}`
      if (bucket) url += `&bucket=${encodeURIComponent(bucket)}`
      if (sortBy) url += `&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir || 'desc')}`
      if (filterMode) url += `&filterMode=${encodeURIComponent(filterMode)}`
      Vue.auth.fetch({ url, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getDriverConsumptionTimeline(orgId, { period = '30d', scope = 'self' } = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/organization/${orgId}/dashboard/driver-consumption-timeline?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getDriverConsumptionBreakdown(orgId, { period = '30d', scope = 'self' } = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/organization/${orgId}/dashboard/driver-consumption-breakdown?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`,
        method: 'get',
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  getChargerDetails(orgId, chargingPointId, { period = '30d', scope = 'self' } = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/organization/${orgId}/dashboard/charger/${encodeURIComponent(chargingPointId)}?period=${encodeURIComponent(period)}&scope=${encodeURIComponent(scope)}`,
        method: 'get',
      })
        .then(response => {
          resolve(response.data)
        }).catch(err => {
          reject(err)
        })
    })
  }
}

export default new OrganizationDashboardRepository()
